import {
  DollarSign,
  LogOut,
  Moon,
  PanelLeft,
  Plus,
  Sun,
} from 'lucide-react'
import type { NavigateFunction } from 'react-router-dom'

import { logout as logoutApi } from '@/api/services/auth.service'
import { queryClient } from '@/main'
import { useAuthStore } from '@/stores/useAuthStore'
import { useUIStore } from '@/stores/useUIStore'
import type { Theme } from '@/stores/useUIStore'
import type { UsePermissionsResult } from '@/hooks/usePermissions'

export interface CommandAction {
  id: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  /** Extra terms to match against when filtering (besides the label). */
  keywords?: string[]
  /** Runs when the action is selected. The palette closes afterwards. */
  perform: () => void
}

export interface CommandActionContext {
  navigate: NavigateFunction
  theme: Theme
  can: UsePermissionsResult['can']
}

/**
 * App-level commands surfaced in the command palette alongside navigation.
 * Each action self-filters via an optional availability check so restricted
 * roles never see actions they can't perform (mirrors route permissions).
 */
export function buildCommandActions(ctx: CommandActionContext): CommandAction[] {
  const { navigate, theme, can } = ctx
  const actions: CommandAction[] = []

  actions.push({
    id: 'toggle-theme',
    label: theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme',
    icon: theme === 'dark' ? Sun : Moon,
    keywords: ['theme', 'dark', 'light', 'appearance', 'mode'],
    perform: () => {
      const { theme: current, setTheme } = useUIStore.getState()
      setTheme(current === 'dark' ? 'light' : 'dark')
    },
  })

  actions.push({
    id: 'toggle-sidebar',
    label: 'Toggle sidebar',
    icon: PanelLeft,
    keywords: ['sidebar', 'collapse', 'expand', 'menu', 'nav'],
    perform: () => useUIStore.getState().toggleSidebar(),
  })

  if (can('users', 'create')) {
    actions.push({
      id: 'new-user',
      label: 'New user',
      icon: Plus,
      keywords: ['create', 'add', 'user', 'invite'],
      perform: () => navigate('/users/new'),
    })
  }

  if (can('roles', 'create')) {
    actions.push({
      id: 'new-role',
      label: 'New role',
      icon: Plus,
      keywords: ['create', 'add', 'role', 'permission'],
      perform: () => navigate('/roles/new'),
    })
  }

  // Quick create. The pages that create in a dialog open it from `?new=1`.
  const quickCreate: Array<{
    id: string
    label: string
    to: string
    keywords: string[]
    allowed: boolean
  }> = [
    {
      id: 'new-company',
      label: 'New company',
      to: '/companies/setup-wizard/drafts/new',
      keywords: ['create', 'add', 'company', 'setup'],
      allowed:
        can('companies', 'create') && can('import_setup', 'read') && can('import_setup', 'update'),
    },
    {
      id: 'new-board',
      label: 'New board',
      to: '/boards?new=1',
      keywords: ['create', 'add', 'board', 'kanban', 'task'],
      allowed: can('boards', 'read') && can('boards', 'create'),
    },
    {
      id: 'new-leave',
      label: 'Request leave',
      to: '/my-leaves?new=1',
      keywords: ['create', 'new', 'leave', 'holiday', 'vacation', 'time off'],
      allowed: can('leave', 'create_self'),
    },
    {
      id: 'new-expense',
      label: 'New expense',
      to: '/expenses?new=1',
      keywords: ['create', 'add', 'expense', 'receipt', 'cost'],
      allowed: can('expenses', 'read') && can('expenses', 'create'),
    },
  ]
  for (const { id, label, to, keywords, allowed } of quickCreate) {
    if (allowed) {
      actions.push({ id, label, icon: Plus, keywords, perform: () => navigate(to) })
    }
  }

  if (can('payroll', 'read_org')) {
    actions.push({
      id: 'go-payroll',
      label: 'Go to Payroll',
      icon: DollarSign,
      keywords: ['payroll', 'salary', 'pay'],
      perform: () => navigate('/payroll'),
    })
  }

  actions.push({
    id: 'logout',
    label: 'Log out',
    icon: LogOut,
    keywords: ['logout', 'sign out', 'signout', 'exit', 'leave'],
    perform: () => {
      logoutApi().finally(() => {
        queryClient.clear()
        useAuthStore.getState().logout()
        navigate('/login', { replace: true })
      })
    },
  })

  return actions
}
